import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { getDateRangeBounds } from "../../utils/dateUtils";
import { SCHEDULE_DATE_RANGE_OPTIONS } from "./constants";
import { Toast, useToast } from "../../components/Toast";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

const PERIOD_MAP = {
  "Last 7 Days":   "the past 7 days",
  "Last 14 Days":  "the past 14 days",
  "Last 28 Days":  "the past 28 days",
  "Last 3 Months": "the past 3 months",
  "This Week":     "this week",
  "MTD":           "month to date",
};

function getDynamicDesc(base, dateRange, appliedCustomStart, appliedCustomEnd) {
  const period = (dateRange === "Custom Range" && appliedCustomStart && appliedCustomEnd)
    ? "the selected date range"
    : (PERIOD_MAP[dateRange] || "the selected period");
  return `${base} for ${period}.`;
}

const TEMPLATES = [
  { id:1, icon:"📊", title:"Performance Summary", desc:null, baseDesc:"Spend, revenue, impressions, and conversions", tags:["Spend","Revenue","Impressions","Conversions"] },
  { id:2, icon:"🎯", title:"Conversion Detail Report", desc:null, baseDesc:"Every step of each conversion journey, with creative, content, cost, and revenue", tags:["Journey","Cost","Revenue","Creative","Content"] },
  // { id:3, icon:"🎙️", title:"Podcast Performance Report", desc:null, baseDesc:"Ranked list of podcasts by conversions, spend, and cost-per-result", tags:["Podcasts","CPR","Spend"] },
  { id:5, icon:"💰", title:"Monthly Budget Pacing Report", desc:null, baseDesc:"Budget utilization, daily spend rate, pacing status, and projected end-of-month totals", tags:["Budget","Pacing","Forecast"] },
  { id:6, icon:"🏆", title:"Creative Performance Comparison", desc:null, baseDesc:"Side-by-side comparison of all ad variants: conversion rate, ROAS, and lift vs. baseline", tags:["Variants","ROAS","Lift"] },
];

export function TemplatesTab({ dateRange, appliedCustomStart, appliedCustomEnd, schedules, schedulesLoading, setSchedules }) {
  const { effectiveAdvertiserId: advertiserId, user, canManageAdvertiserData } = useAuth();
  const [scheduleModal, setScheduleModal] = useState(null);
  const [schedDateRange, setSchedDateRange] = useState(7);
  const [freq, setFreq] = useState("Weekly");
  const [day, setDay] = useState("Monday");
  const [fmt, setFmt] = useState("PDF");
  const [firstReportAt, setFirstReportAt] = useState("");
  const [recipients, setRecipients] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [schedSaving, setSchedSaving] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [toast, showToast] = useToast(5000);

  const enabledTemplates = TEMPLATES.filter(t => [1, 2, 3, 5].includes(t.id));
  const scheduledTemplateIds = new Set(schedules.map(s => s.template_id));

  const openScheduleModal = (template) => {
    if (!canManageAdvertiserData) return;
    setSchedDateRange(7);
    setFreq("Weekly");
    setDay("Monday");
    setFmt("PDF");
    setFirstReportAt("");
    setRecipients(user?.email || "");
    setEmailSubject(template.title);
    setScheduleModal(template);
  };

  const openEditModal = (schedule) => {
    if (!canManageAdvertiserData) return;
    const template = TEMPLATES.find(t => t.id === schedule.template_id);
    setSchedDateRange(schedule.date_range_preset);
    setFreq(schedule.frequency === "weekly" ? "Weekly" : "Monthly");
    setDay(schedule.delivery_day || "Monday");
    setFmt(schedule.format);
    setFirstReportAt(schedule.first_report_at);
    setRecipients((schedule.recipients || []).join(", "));
    setEmailSubject(schedule.email_subject || "");
    setEditingSchedule(schedule);
    setScheduleModal(template);
  };

  const handleDownload = async (template) => {
    // In demo mode, generate and download the CSV immediately instead of
    // queuing a backend job (which would never run).
    if (process.env.REACT_APP_DEMO_MODE === "true") {
      setDownloadingIds(prev => new Set(prev).add(template.id));
      const { downloadDemoReport } = require("../../mocks/data/reportDownloads");
      const ok = downloadDemoReport(template, dateRange, appliedCustomStart, appliedCustomEnd);
      setDownloadingIds(prev => { const s = new Set(prev); s.delete(template.id); return s; });
      if (ok) showToast("Your report is downloading now.");
      else showToast("Report template not available in demo.", "error");
      return;
    }

    if (!supabase || advertiserId == null) { showToast("Not connected to database.", "error"); return; }
    setDownloadingIds(prev => new Set(prev).add(template.id));
    const { startDate, endDate } = getDateRangeBounds(dateRange, appliedCustomStart, appliedCustomEnd);
    const { error } = await supabase.from("report_downloads").insert({
      advertiser_id: advertiserId,
      template_id: template.id,
      format: "CSV",
      start_date: startDate,
      end_date: endDate,
      recipients: user?.email ? [user.email] : [],
      email_subject: template.title,
      created_by: user?.id ?? null,
    });
    setDownloadingIds(prev => { const s = new Set(prev); s.delete(template.id); return s; });
    if (error) showToast(`Failed to queue download: ${error.message}`, "error");
    else showToast("You'll receive an email with a link to download shortly.");
  };

  const closeScheduleModal = () => { setScheduleModal(null); setEditingSchedule(null); };

  // `requestClose` is the schedule modal's own: a successful save plays its exit, then closes it.
  const handleConfirmSchedule = async (requestClose) => {
    if (!canManageAdvertiserData) {
      showToast("You don't have permission to manage report schedules.", "error");
      return;
    }
    if (!supabase || advertiserId == null) { showToast("Not connected to database.", "error"); return; }
    const recipientList = recipients.split(",").map(r => r.trim()).filter(Boolean);
    if (recipientList.length === 0) { showToast("Please enter at least one recipient email.", "error"); return; }
    if (!firstReportAt) { showToast("Please choose a first report date.", "error"); return; }
    setSchedSaving(true);
    const nextRunAt = new Date(`${firstReportAt}T00:00:00.000Z`).toISOString();
    const payload = {
      format: fmt,
      date_range_preset: schedDateRange,
      frequency: freq.toLowerCase(),
      delivery_day: freq === "Weekly" ? day : null,
      first_report_at: firstReportAt,
      next_run_at: nextRunAt,
      recipients: recipientList,
      email_subject: emailSubject || scheduleModal.title,
    };
    const { error } = editingSchedule
      ? await supabase.from("report_schedules").update(payload).eq("id", editingSchedule.id)
      : await supabase.from("report_schedules").insert({ ...payload, advertiser_id: advertiserId, template_id: scheduleModal.id, is_active: true, created_by: user?.id ?? null });
    setSchedSaving(false);
    if (error) {
      showToast(`Failed to save schedule: ${error.message}`, "error");
    } else {
      const { data } = await supabase.from("report_schedules").select("*").eq("advertiser_id", advertiserId).order("created_at", { ascending: false });
      setSchedules(data || []);
      requestClose();
      showToast(editingSchedule ? "Schedule updated." : "Schedule saved. You'll receive the first report on " + firstReportAt + ".");
    }
  };

  const handleToggleActive = async (schedule) => {
    if (!canManageAdvertiserData) return;
    const { error } = await supabase.from("report_schedules").update({ is_active: !schedule.is_active }).eq("id", schedule.id);
    if (error) showToast(`Failed to update schedule: ${error.message}`, "error");
    else setSchedules(prev => prev.map(s => s.id === schedule.id ? { ...s, is_active: !s.is_active } : s));
  };

  const handleRemoveSchedule = async (schedule) => {
    if (!canManageAdvertiserData) return;
    const { error } = await supabase.from("report_schedules").delete().eq("id", schedule.id);
    if (error) showToast(`Failed to remove schedule: ${error.message}`, "error");
    else { setSchedules(prev => prev.filter(s => s.id !== schedule.id)); setConfirmRemoveId(null); showToast("Schedule removed."); }
  };

  const templateNameById = Object.fromEntries(TEMPLATES.map(t => [t.id, t.title]));

  const pillBtnStyle = (active) => ({
    padding:"7px 16px", borderRadius:20, border:"none", cursor:"pointer", fontSize:12, fontWeight:600,
    background: active ? "#F90091" : "#F0F0F0",
    color: active ? "#fff" : "#626262",
  });

  const renderTemplateCard = (t) => {
    const desc = t.baseDesc ? getDynamicDesc(t.baseDesc, dateRange, appliedCustomStart, appliedCustomEnd) : t.desc;
    const isScheduled = scheduledTemplateIds.has(t.id);
    const isDownloading = downloadingIds.has(t.id);
    return (
      <div key={t.id} style={{ background:"#FFFFFF", border:`1px solid ${isScheduled ? "#F90091" : "#E2E2E2"}`, borderRadius:14, padding:18, boxShadow:"0 2px 10px rgba(0,0,0,0.06)" }}>
        <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
          <div style={{ width:40, height:40, borderRadius:10, background:"#FAFAFA", border:"1px solid #E2E2E2", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{t.icon}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
              <span style={{ fontWeight:600, color:"#151515", fontSize:14 }}>{t.title}</span>
              {isScheduled && (
                <span style={{ fontSize:9, padding:"2px 7px", borderRadius:20, background:"#d8fffa", color:"#208a3e", fontWeight:700, letterSpacing:0.5, textTransform:"uppercase" }}>Scheduled</span>
              )}
            </div>
            <p style={{ color:"#626262", fontSize:12, lineHeight:1.5, margin:"0 0 10px" }}>{desc}</p>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
              {t.tags.map(tag => (
                <span key={tag} style={{ fontSize:10, padding:"2px 8px", borderRadius:20, background:"#F5F5F5", color:"#7b7b7b", border:"1px solid #E2E2E2" }}>{tag}</span>
              ))}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button
                onClick={() => handleDownload(t)}
                disabled={isDownloading}
                style={{
                  padding:"7px 14px", borderRadius:8, fontSize:12, cursor: isDownloading ? "default" : "pointer",
                  display:"flex", alignItems:"center", gap:6,
                  background: isDownloading ? "#FFF5FB" : "transparent",
                  border:`1px solid ${isDownloading ? "#F90091" : "#E2E2E2"}`,
                  color: isDownloading ? "#F90091" : "#626262",
                  transition:"background 0.15s, border-color 0.15s, color 0.15s",
                }}
              >
                {isDownloading ? (
                  <><span style={{ display:"inline-block", width:10, height:10, border:"2px solid #F90091", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />Queuing…</>
                ) : "⬇ Download"}
              </button>
              <button
                onClick={isScheduled ? undefined : () => openScheduleModal(t)}
                disabled={isScheduled || !canManageAdvertiserData}
                style={{ padding:"7px 14px", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, color:"#626262", fontSize:12, cursor: isScheduled || !canManageAdvertiserData ? "default" : "pointer", display:"flex", alignItems:"center", gap:6, opacity: isScheduled || !canManageAdvertiserData ? 0.4 : 1 }}
              >
                🕐 Schedule
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const inputStyle = {
    width:"100%", background:"#FAFAFA", border:"1px solid #E2E2E2",
    borderRadius:8, padding:"10px 14px", color:"#151515",
    fontSize:13, marginBottom:20, boxSizing:"border-box",
  };

  return (
    <div>
      <div style={{ marginBottom:22 }}>
        <p style={{ color:"#626262", fontSize:13 }}>Download a one-time export or schedule recurring delivery to your inbox.</p>
      </div>

      {!schedulesLoading && schedules.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Active Schedules</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {schedules.map(s => (
              <div key={s.id} style={{ background:"#FFFFFF", border:`1px solid ${s.is_active ? "#E2E2E2" : "#F0F0F0"}`, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3, flexWrap:"wrap" }}>
                    <span style={{ fontWeight:600, color: s.is_active ? "#151515" : "#B8B8B8", fontSize:13 }}>
                      {templateNameById[s.template_id] || `Template ${s.template_id}`}
                    </span>
                    <span style={{ fontSize:9, padding:"2px 7px", borderRadius:20, background: s.is_active ? "#d8fffa" : "#F5F5F5", color: s.is_active ? "#208a3e" : "#B8B8B8", fontWeight:700, letterSpacing:0.5, textTransform:"uppercase" }}>
                      {s.is_active ? "Active" : "Paused"}
                    </span>
                  </div>
                  <div style={{ fontSize:11, color:"#7b7b7b", display:"flex", gap:12, flexWrap:"wrap" }}>
                    <span>{s.frequency === "weekly" ? `Weekly · ${s.delivery_day}` : "Monthly"}</span>
                    <span>·</span>
                    <span>Last {s.date_range_preset} Days</span>
                    <span>·</span>
                    <span>{s.format}</span>
                    <span>·</span>
                    <span>{(s.recipients || []).length} recipient{(s.recipients || []).length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, flexShrink:0, alignItems:"center" }}>
                  {confirmRemoveId === s.id ? (
                    <>
                      <span style={{ fontSize:11, color:"#7b7b7b" }}>Remove this schedule?</span>
                      <button onClick={() => handleRemoveSchedule(s)} style={{ padding:"5px 12px", background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:7, color:"#e53935", fontSize:11, cursor:"pointer", fontWeight:600 }}>Yes, remove</button>
                      <button onClick={() => setConfirmRemoveId(null)} style={{ padding:"5px 12px", background:"transparent", border:"1px solid #E2E2E2", borderRadius:7, color:"#626262", fontSize:11, cursor:"pointer" }}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => openEditModal(s)} disabled={!canManageAdvertiserData} style={{ padding:"5px 12px", background:"transparent", border:"1px solid #E2E2E2", borderRadius:7, color:"#626262", fontSize:11, cursor: canManageAdvertiserData ? "pointer" : "not-allowed", opacity: canManageAdvertiserData ? 1 : 0.45 }}>Edit</button>
                      <button onClick={() => handleToggleActive(s)} disabled={!canManageAdvertiserData} style={{ padding:"5px 12px", background:"transparent", border:"1px solid #E2E2E2", borderRadius:7, color:"#626262", fontSize:11, cursor: canManageAdvertiserData ? "pointer" : "not-allowed", opacity: canManageAdvertiserData ? 1 : 0.45 }}>{s.is_active ? "Pause" : "Resume"}</button>
                      <button onClick={() => canManageAdvertiserData && setConfirmRemoveId(s.id)} disabled={!canManageAdvertiserData} style={{ padding:"5px 12px", background:"transparent", border:"1px solid #fca5a5", borderRadius:7, color:"#e53935", fontSize:11, cursor: canManageAdvertiserData ? "pointer" : "not-allowed", opacity: canManageAdvertiserData ? 1 : 0.45 }}>Remove</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:"grid", gap:14 }}>
        {enabledTemplates.map(renderTemplateCard)}
      </div>

      {scheduleModal && (
        <ScheduleModal onClose={closeScheduleModal} saving={schedSaving}>
          {(requestClose) => (<>
            <div style={{ padding:"22px 24px 16px", borderBottom:"1px solid #F0F0F0", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
              <div>
                <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{editingSchedule ? "Edit Schedule" : "Schedule Report"}</div>
                <div id="schedule-modal-title" style={{ fontWeight:600, color:"#151515", fontSize:16, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>{scheduleModal.title}</div>
              </div>
              <button onClick={requestClose} aria-label="Close" style={{ background:"none", border:"none", color:"#626262", fontSize:20, cursor:"pointer" }}>✕</button>
            </div>

            <div style={{ padding:24, overflowY:"auto", flex:1 }}>
              <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Frequency</div>
              <div style={{ display:"flex", gap:8, marginBottom:20 }}>
                {["Weekly","Monthly"].map(f => <button key={f} onClick={() => setFreq(f)} style={pillBtnStyle(freq===f)}>{f}</button>)}
              </div>

              {freq === "Weekly" && (
                <>
                  <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Date Range</div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
                    {SCHEDULE_DATE_RANGE_OPTIONS.map(days => (
                      <button key={days} onClick={() => setSchedDateRange(days)} style={pillBtnStyle(schedDateRange===days)}>Last {days} Days</button>
                    ))}
                  </div>
                </>
              )}

              {freq === "Weekly" && (
                <>
                  <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Send Every</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20 }}>
                    {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => (
                      <button key={d} onClick={() => setDay(d)} style={{ padding:"6px 12px", borderRadius:20, border:`1px solid ${day===d ? "#F90091" : "#E2E2E2"}`, cursor:"pointer", fontSize:11, background: day===d ? "#FFF5FB" : "#FAFAFA", color: day===d ? "#F90091" : "#626262" }}>{d}</button>
                    ))}
                  </div>
                </>
              )}

              <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>First Report Date</div>
              <input type="date" value={firstReportAt} onChange={e => setFirstReportAt(e.target.value)} style={inputStyle} />

              <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Format</div>
              <div style={{ display:"flex", gap:8, marginBottom:20 }}>
                {["PDF","CSV"].map(f => <button key={f} onClick={() => setFmt(f)} style={pillBtnStyle(fmt===f)}>{f}</button>)}
              </div>

              <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Recipients</div>
              <div style={{ fontSize:11, color:"#B8B8B8", marginBottom:6 }}>Comma-separated email addresses.</div>
              <input type="text" value={recipients} onChange={e => setRecipients(e.target.value)} placeholder="email@example.com, another@example.com" style={inputStyle} />

              <div style={{ fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Email Subject</div>
              <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} style={inputStyle} />

              <button
                onClick={() => handleConfirmSchedule(requestClose)}
                disabled={schedSaving}
                style={{ width:"100%", padding:"13px 0", background: schedSaving ? "#F9009199" : "#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:14, fontWeight:600, cursor: schedSaving ? "default" : "pointer" }}
              >
                {schedSaving ? "Saving…" : editingSchedule ? "Save Changes →" : "Confirm Schedule →"}
              </button>
            </div>
          </>)}
        </ScheduleModal>
      )}

      <Toast {...toast} />
    </div>
  );
}

// The schedule modal, mounted only while it is open so it owns its motion hook. Escape
// closes it, except while a save is in flight. `children` receives `requestClose`.
function ScheduleModal({ onClose, saving, children }) {
  const motion = useModalMotion(onClose, { dismissible: !saving });
  return (
    <ModalShell motion={motion} labelledBy="schedule-modal-title" maxWidth={440} panelStyle={{ overflow:"hidden", display:"flex", flexDirection:"column" }}>
      {children(motion.requestClose)}
    </ModalShell>
  );
}
